import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { BlotterAnalyticCategory } from 'models/analytics';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { addonTabCreatedThunk } from 'state/addonForm/addonForm.thunks';
import { getBlotterItemDocuments, getBlotterPanelActions } from 'state/blotters/blotterPanel/blotterPanel.selector';
import { productModificationTabCreatedThunk } from 'state/productModificationForm/productModificationForm.thunks';
import { secondaryMarketQuoteByIsinReloadRequested } from 'state/secondaryMarket/realtimeQuote/quote';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { tradeAllocationTabCreatedThunk } from 'state/tradeAllocationForm/tradeAllocationForm.thunks';
import { tradeCancellationWorkflowInit } from 'state/trades/cancellations/cancellations.actions';
import { workspaceTabRepriceRequested } from 'state/workspaces/workspace.actions';
import { workspaceTabReloadRequestedThunk } from 'state/workspaces/workspace.thunks';

import type {
  BlotterPanelFooterDispatchProps,
  BlotterPanelFooterOwnProps,
  BlotterPanelFooterStateProps,
} from './BlotterPanelFooter';
import { BlotterPanelFooterComponent } from './BlotterPanelFooter';

export const mapStateToProps = (state: AppState): BlotterPanelFooterStateProps => ({
  actions: getBlotterPanelActions(state),
  documents: getBlotterItemDocuments(state),
});

const postTradeTabName = (item: IBlotterItem) => {
  if (item.isin && item.isin !== '') {
    return item.isin;
  }

  if (item.fileCode && item.fileCode !== '') {
    return item.fileCode;
  }

  return item.productId;
};
export const mapDispatchToProps = (
  dispatch: ThunkDispatch,
  { blotterItem, blotterType }: BlotterPanelFooterOwnProps,
): BlotterPanelFooterDispatchProps => {
  return {
    onNewStaticRepriceInitiated() {
      const id = blotterItem.productId ? blotterItem.productId : blotterItem.quoteId;
      const idType = blotterItem.productId ? IdType.ProductId : IdType.QuoteId;
      dispatch(
        workspaceTabRepriceRequested(
          id,
          idType,
          blotterItem.quoteId,
          blotterItem.pricerRfqId,
          'static',
          blotterItem.status !== 'Accepted',
          blotterItem.processingChain,
        ),
      );
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Reload (New Static Screen)');
    },
    onNewDynamicRepriceInitiated() {
      const id = blotterItem.productId ? blotterItem.productId : blotterItem.quoteId;
      const idType = blotterItem.productId ? IdType.ProductId : IdType.QuoteId;
      dispatch(
        workspaceTabRepriceRequested(
          id,
          idType,
          blotterItem.quoteId,
          blotterItem.pricerRfqId,
          'dynamic',
          blotterItem.status !== 'Accepted',
          blotterItem.processingChain,
        ),
      );
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Reload (New Dynamic Screen)');
    },
    onBatchReloadInitiated(tabName: string) {
      dispatch(
        workspaceTabReloadRequestedThunk(
          'newBatch',
          blotterItem.productId,
          blotterItem.quoteId,
          blotterItem.pricerRfqId,
          blotterItem.processingChain,
          true,
          blotterItem.externalQuoteId,
          tabName,
        ),
      );
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Reprice batch');
    },
    onAddonInitiated() {
      dispatch(addonTabCreatedThunk(blotterItem.productId, blotterItem.quoteId, postTradeTabName(blotterItem)));
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Addon');
    },
    onSecondaryReload() {
      dispatch(secondaryMarketQuoteByIsinReloadRequested(blotterItem.isin, blotterType));
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Open in secondary');
    },
    onCancellationWorkflowInitiated() {
      dispatch(
        tradeCancellationWorkflowInit(
          blotterItem.blotterItemType === 'DlpProductTrade'
            ? (blotterItem.tradeReference as string)
            : blotterItem.tradeId,
          blotterItem,
          blotterType,
        ),
      );
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Trade cancellation workflow initiated');
    },
    onProductModificationInitiated() {
      dispatch(productModificationTabCreatedThunk(blotterItem.productId, postTradeTabName(blotterItem)));
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'Modify product (Rfq)');
    },
    onNewTradeAllocationInitiated() {
      dispatch(
        tradeAllocationTabCreatedThunk(
          blotterItem.tradeId,
          postTradeTabName(blotterItem),
          blotterItem.productCode,
          blotterItem.wrapper,
          blotterItem.productId,
          blotterItem.quoteId,
          blotterItem.isin,
          blotterItem.fileCode,
        ),
      );
      logAnalytics.trackEvent(getAnalyticsCategory(blotterType), 'New Trade allocation');
    },
  };
};

function getAnalyticsCategory(blotterType: BlotterType): BlotterAnalyticCategory {
  switch (blotterType) {
    case 'trades':
      return 'Blotter My Trades';
    case 'products':
      return 'Blotter My Products';
    case 'quotes':
      return 'Blotter My Quotes';
    case 'dlpProducts':
      return 'Blotter My flow products';
    case 'dlpProductTrades':
      return 'Blotter My flow products trades';
    default:
      throw new Error(`Unknown blotter type : ${blotterType}`);
  }
}

export const BlotterPanelFooter = connect(mapStateToProps, mapDispatchToProps)(BlotterPanelFooterComponent);
